import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import UploadIcon from '@mui/icons-material/Upload'
import { useTranslation } from 'react-i18next'

import {
  useGetBuildingsQuery,
  useGetDetailsQuery
} from '@/api'
import {
  useGetIncidentByIdQuery,
  useGetStatusesQuery,
  useUpdateIncidentMutation,
  useAddNewIncidentMutation
} from '../incidents-api'

// DialogForm.propTypes = {
//   currentId: PropTypes.string,
//   onClose: PropTypes.func,
//   open: PropTypes.bool
// }

const DialogForm = ({
  currentId,
  open = false,
  onClose = () => {},
  buildingId 
}) => {
  const { t } = useTranslation()
  const [createIncident, { isLoading, isSuccess }] = useAddNewIncidentMutation()
  const { data: statuses, isSuccess: statusesSuccess } = useGetStatusesQuery()
  const { data: user } = useGetDetailsQuery()
  const [editIncident, { isSuccess: editSuccess }] = useUpdateIncidentMutation(currentId)
  const { data: buildings } = useGetBuildingsQuery()
  const { data: incidentData } = useGetIncidentByIdQuery(currentId, {
    skip: !currentId,
    refetchOnMountOrArgChange: true
  })
  const isAdmin = user?.roles.find((role) => role.id === 1)

  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [form, setForm] = useState({
    incidentTheme: '',
    incidentDescription: '',
    status: 1
  })

  const handleChange = ({ target }) => {
    setForm({
      ...form,
      [target.name]: target.value
    })

    if (target.name === 'incidentDescription') {
      setForm({
        ...form,
        [target.name]: target.value.slice(0, 150)
      })
    }
  }

  const handleClose = () => {
    setForm({
      incidentTheme: '',
      incidentDescription: '',
      status: 1
    })
    onClose()
  }

  const handleSnackOpen = () => {
    setOpenSnackbar(true)
  }

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setOpenSnackbar(false)
  }

  const onSuccess = () => {
    setForm({
      incidentTheme: '',
      incidentDescription: '',
      status: 1
    })
    onClose()
    handleSnackOpen()
  }

  const handleSubmit = async () => {
    if (currentId && incidentData) {
      try {
        await editIncident({
          currentId,
          id: currentId,
          building_id: +buildingId,
          theme: form.incidentTheme,
          description: form.incidentDescription,
          user_id: incidentData?.user_id,
          created_at: incidentData?.created_at,
          status_id: form?.status,
          updated_at: new Date(Date.now()),
          operator_id: incidentData?.operator_id
        })
        onSuccess()
      } catch (error) {
        console.log(error)
      }
    } else {
      try {
        await createIncident({
          building_id: +buildingId,
          theme: form.incidentTheme,
          status_id: form.status,
          description: form.incidentDescription
        })
        onSuccess()
      } catch (error) {
        console.log(error)
      }
    }
  }

  useEffect(() => {
    if (currentId && incidentData) {
      setForm({
        building: incidentData?.building_id,
        incidentTheme: incidentData?.theme,
        status: incidentData?.status?.id,
        incidentDescription: incidentData?.description
      })
    }
  }, [buildings, currentId, incidentData])

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth>
        {isLoading && <LinearProgress />}
        <DialogTitle>
          {t('creating_a_new_incident')}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isAdmin && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t('select_status')}</InputLabel>
              <Select
                name="status"
                value={form?.status || ''}
                onChange={handleChange}
                input={<OutlinedInput label={t('select_status')} />}
              >
                {statusesSuccess &&
                  statuses.map((status) => (
                    <MenuItem key={status.id} value={status?.id}>
                      {status.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>{t('incident_text_theme')}</InputLabel>
            <OutlinedInput
              name="incidentTheme"
              autoComplete="incidentTheme"
              value={form.incidentTheme}
              onChange={handleChange}
              label={t('incident_text_theme')}
            />
          </FormControl>
          <TextField
            name="incidentDescription"
            autoComplete="incidentDescription"
            multiline
            fullWidth
            rows={5}
            placeholder={t(
              'enter_a_description_of_the_appeal_(max. 150 characters)'
            )}
            value={form.incidentDescription}
            onChange={handleChange}
          />
          <Stack
            sx={{
              display: "flex",
              alignItems: "flex-end"
            }}>
            <Typography>{form.incidentDescription.length} / 150</Typography>
          </Stack>
          <Button
            sx={{ mt: 1.5 }}
            startIcon={<UploadIcon />}
            variant="contained"
            component="label"
            size="small"
            color="info"
          >
            {t('incidents_file_add')}
            <input hidden accept="image/*" multiple type="file" />
          </Button>
          <Button
            sx={{ mt: 3 }}
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={!form.incidentTheme || !form.incidentDescription}
          >
            {currentId ? t('edit') : t('incidents_create')}
          </Button>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        <Alert
          sx={{ width: '100%' }}
          severity={isSuccess || editSuccess ? 'success' : 'error'}
          variant="filled"
          onClose={handleSnackClose}
        >
          {isSuccess || editSuccess
            ? t('change_saved!')
            : t('something_went_wrong')}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DialogForm
